/**
 * Analytics entry point. `initAnalytics(userId)` wires the session tracker, the
 * event queue, and the 30s heartbeat together; `shutdownAnalytics()` tears them
 * down (test cleanup / sign-out).
 *
 * The heartbeat lives here rather than in session.ts because it crosses module
 * boundaries (pulls active ms from session, pushes idle_ping through track).
 */
import { startSession } from './session.js';
import { startQueue } from './queue.js';
import { bindRuntime, unbindRuntime, track } from './track.js';

const HEARTBEAT_INTERVAL_MS = 30_000;

interface AnalyticsHandle {
  shutdown(): void;
}

let active: AnalyticsHandle | null = null;

/**
 * Boot analytics for `userId`. `initialRoute` must be the route TEMPLATE the
 * caller is rendering (e.g. `/tools/jobs/:id`) so the first session_start event
 * has the right cardinality. No default — passing a concrete path here would
 * silently pollute the route field.
 *
 * Idempotent: returns the existing handle if already booted. Callers
 * switching users must `shutdownAnalytics()` first.
 */
export function initAnalytics(userId: string, initialRoute: string): AnalyticsHandle {
  if (active) return active;

  const session = startSession(userId);
  const queue = startQueue();
  bindRuntime({ session, queue, currentRoute: initialRoute });

  const heartbeat = window.setInterval(() => {
    const activeMs = session.flushActiveMs();
    track.idle(activeMs);
  }, HEARTBEAT_INTERVAL_MS);

  active = {
    shutdown() {
      window.clearInterval(heartbeat);
      session.destroy();
      queue.destroy();
      unbindRuntime();
      active = null;
    },
  };
  return active;
}

export function shutdownAnalytics(): void {
  if (active) active.shutdown();
}

export { track } from './track.js';
